import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional fallback UI. Defaults to a built-in error screen. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Called when an error is caught — use to report to LogRocket etc. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

/**
 * Route-level error boundary for EverydayOS web apps.
 *
 * Catches React render errors, logs them, and renders a recovery UI
 * instead of crashing the full application.
 *
 * Usage:
 *   <ErrorBoundary onError={logger.error}>
 *     <Dashboard />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[EverydayOS] Uncaught render error:', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset);
    }

    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-slate-950 px-6 text-center">
        <div className="max-w-md w-full">
          <div className="text-4xl mb-4">⚠️</div>
          <h1 className="text-xl font-black text-slate-900 dark:text-white mb-2">
            Something went wrong
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            An unexpected error occurred. Your data is safe — refreshing should resolve this.
          </p>
          {(import.meta as any).env?.DEV && (
            <pre className="text-left text-xs bg-slate-100 dark:bg-slate-900 text-red-600 dark:text-red-400 rounded-xl p-4 mb-6 overflow-auto max-h-40 border border-red-200 dark:border-red-900">
              {error.message}
            </pre>
          )}
          <div className="flex gap-3 justify-center">
            <button
              onClick={this.reset}
              className="h-10 px-5 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="h-10 px-5 rounded-xl text-sm font-bold bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
